import { useState, useEffect } from 'react';
import { useArticles } from '../context/ArticleContext';
import { Article } from '../services/articleService';
import { ArticleFormData } from '../types/article';
import { toast } from 'react-hot-toast';

export const useArticleManagement = () => {
  const {
    articles,
    loading: articlesLoading,
    error: articlesError,
    fetchAllArticles,
    createArticle,
    updateArticle,
    deleteArticle,
  } = useArticles();

  const [articleFormOpen, setArticleFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [selectedArticle, setSelectedArticle] = useState<ArticleFormData | null>(null);

  useEffect(() => {
    fetchAllArticles();
  }, [fetchAllArticles]);

  const handleAddArticle = () => {
    setFormMode('add');
    setSelectedArticle(null);
    setArticleFormOpen(true);
  };

  const handleEditArticle = (article: Article) => {
    setSelectedArticle(convertArticleToFormData(article));
    setFormMode('edit');
    setArticleFormOpen(true);
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteArticle(id);
      await fetchAllArticles();
      toast.success('Статья успешно удалена');
    } catch (error) {
      console.error('Ошибка при удалении статьи:', error);
      toast.error('Ошибка при удалении статьи');
    }
  };

  const handleArticleSubmit = async (formData: FormData) => {
    try {
      if (selectedArticle && selectedArticle.id) {
        await updateArticle(selectedArticle.id.toString(), formData);
        toast.success('Статья обновлена');
      } else {
        await createArticle(formData);
        toast.success('Статья создана');
      }
      await fetchAllArticles();
      setArticleFormOpen(false);
    } catch (error) {
      console.error('Ошибка при сохранении статьи:', error);
      toast.error('Ошибка при сохранении статьи');
    }
  };

  const convertArticleToFormData = (article: Article): ArticleFormData => {
    let pdfFileData = article.pdfFile;
    if (typeof article.pdfFile === 'string') {
      try {
        pdfFileData = JSON.parse(article.pdfFile);
      } catch (e) {
        console.error('Error parsing pdfFile JSON:', e);
      }
    }

    return {
      id: article.id,
      title: article.title,
      authors: article.authors?.map(author => author.id) || [],
      abstract: article.abstract,
      keywords: article.keywords,
      doi: article.doi || '',
      edn: article.edn || '',
      issueId: article.issue?.id || 0,
      pages: article.pages ?? '',
      status: article.status,
      submittedDate: article.createdAt,
      pdfFile: pdfFileData
    };
  };

  return {
    articles,
    articlesLoading,
    articlesError,
    articleFormOpen,
    formMode,
    selectedArticle,
    setArticleFormOpen,
    handleAddArticle,
    handleEditArticle,
    handleDeleteArticle,
    handleArticleSubmit,
  };
}; 